import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { RotateCcw, Save, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PoseEditor({ projectId, riggedModelUrl, onSavePose }) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const skeletonRef = useRef(null);
  const modelRef = useRef(null);
  
  const [bones, setBones] = useState([]);
  const [selectedBone, setSelectedBone] = useState(null);
  const [boneRotations, setBoneRotations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = 500;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 1.5, 4);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, 1, 0);
    controls.update();
    controlsRef.current = controls;

    // Lighting - brighter for visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
    directionalLight.position.set(5, 10, 5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
    fillLight.position.set(-5, 5, -5);
    scene.add(fillLight);
    
    const backLight = new THREE.DirectionalLight(0xffffff, 0.5);
    backLight.position.set(0, 5, -10);
    scene.add(backLight);

    // Ground grid
    const gridHelper = new THREE.GridHelper(10, 10, 0x444444, 0x333333);
    scene.add(gridHelper);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Load model
    loadModel();

    // Cleanup
    return () => {
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []);

  // Load the rigged GLB model
  const loadModel = async () => {
    if (!riggedModelUrl || !sceneRef.current) return;
    
    setLoading(true);
    setError(null);

    try {
      const loader = new GLTFLoader();
      
      // Use proxy URL for the rigged model (GLB format)
      const modelUrl = `${API}/api/3d-lab/proxy-model/${projectId}?type=rigged`;
      
      loader.load(
        modelUrl,
        (gltf) => {
          const model = gltf.scene;
          
          // Find skeleton and bones
          const bonesFound = [];
          const initialRotations = {};
          let skeleton = null;
          let skinnedMesh = null;
          let regularMesh = null;
          
          model.traverse((child) => {
            if (child.isBone) {
              bonesFound.push({
                name: child.name,
                bone: child
              });
              initialRotations[child.name] = {
                x: THREE.MathUtils.radToDeg(child.rotation.x),
                y: THREE.MathUtils.radToDeg(child.rotation.y),
                z: THREE.MathUtils.radToDeg(child.rotation.z)
              };
              console.log('Found bone:', child.name);
            }
            if (child.isSkinnedMesh) {
              skinnedMesh = child;
              skeleton = child.skeleton;
              child.castShadow = true;
              child.receiveShadow = true;
              console.log('Found skinned mesh:', child.name);
            }
            if (child.isMesh && !child.isSkinnedMesh) {
              regularMesh = child;
              child.castShadow = true;
              child.receiveShadow = true;
              console.log('Found regular mesh:', child.name);
            }
          });

          // Center the model
          const bbox = new THREE.Box3().setFromObject(model);
          const center = new THREE.Vector3();
          bbox.getCenter(center);
          model.position.x = -center.x;
          model.position.z = -center.z;
          model.position.y = -bbox.min.y; // Feet at ground

          // Add skeleton helper for visualization
          if (skeleton || bonesFound.length > 0) {
            const skeletonHelper = new THREE.SkeletonHelper(model);
            skeletonHelper.visible = true;
            sceneRef.current.add(skeletonHelper);
            skeletonRef.current = skeletonHelper;
          }

          sceneRef.current.add(model);
          modelRef.current = model;
          
          setBones(bonesFound);
          setBoneRotations(initialRotations);
          setLoading(false);
          
          // Auto-select first major bone
          if (bonesFound.length > 0) {
            const mainBones = bonesFound.filter(b => 
              ['spine', 'chest', 'head', 'upper_arm', 'thigh', 'hips'].some(n => 
                b.name.toLowerCase().includes(n)
              )
            );
            if (mainBones.length > 0) {
              setSelectedBone(mainBones[0].name);
            } else {
              setSelectedBone(bonesFound[0].name);
            }
          }
        },
        (progress) => {
          console.log('Loading:', (progress.loaded / progress.total * 100).toFixed(0) + '%');
        },
        (err) => {
          console.error('Error loading model:', err);
          setError('Failed to load model');
          setLoading(false);
        }
      );
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Update bone rotation
  const updateBoneRotation = (boneName, axis, value) => {
    const boneData = bones.find(b => b.name === boneName);
    if (!boneData) return;

    const bone = boneData.bone;
    const radians = THREE.MathUtils.degToRad(value);
    
    if (axis === 'x') bone.rotation.x = radians;
    if (axis === 'y') bone.rotation.y = radians;
    if (axis === 'z') bone.rotation.z = radians;

    setBoneRotations(prev => ({
      ...prev,
      [boneName]: {
        ...prev[boneName],
        [axis]: value
      }
    }));
  };

  // Reset pose
  const resetPose = () => {
    bones.forEach(({ bone }) => {
      bone.rotation.set(0, 0, 0);
    });
    
    const resetRotations = {};
    bones.forEach(({ name }) => {
      resetRotations[name] = { x: 0, y: 0, z: 0 };
    });
    setBoneRotations(resetRotations);
  };

  // Get current pose data
  const getCurrentPose = () => {
    const pose = {};
    bones.forEach(({ name, bone }) => {
      pose[name] = {
        x: THREE.MathUtils.radToDeg(bone.rotation.x),
        y: THREE.MathUtils.radToDeg(bone.rotation.y),
        z: THREE.MathUtils.radToDeg(bone.rotation.z)
      };
    });
    return pose;
  };

  // Group bones by type for easier navigation - handle various naming conventions
  const groupedBones = React.useMemo(() => {
    const groups = {
      'Spine': [],
      'Left Arm': [],
      'Right Arm': [],
      'Left Leg': [],
      'Right Leg': [],
      'Other': []
    };
    
    bones.forEach(b => {
      const name = b.name.toLowerCase();
      
      // Check for spine/torso bones
      if (['root', 'spine', 'chest', 'head', 'neck', 'hips', 'pelvis'].some(n => name.includes(n))) {
        groups['Spine'].push(b);
      }
      // Check for left arm
      else if ((name.includes('l') || name.includes('left')) && 
               ['arm', 'hand', 'shoulder', 'elbow', 'wrist', 'finger'].some(n => name.includes(n))) {
        groups['Left Arm'].push(b);
      }
      // Check for right arm
      else if ((name.includes('r') || name.includes('right')) && 
               ['arm', 'hand', 'shoulder', 'elbow', 'wrist', 'finger'].some(n => name.includes(n))) {
        groups['Right Arm'].push(b);
      }
      // Check for left leg
      else if ((name.includes('l') || name.includes('left')) && 
               ['thigh', 'shin', 'foot', 'leg', 'knee', 'ankle', 'toe'].some(n => name.includes(n))) {
        groups['Left Leg'].push(b);
      }
      // Check for right leg
      else if ((name.includes('r') || name.includes('right')) && 
               ['thigh', 'shin', 'foot', 'leg', 'knee', 'ankle', 'toe'].some(n => name.includes(n))) {
        groups['Right Leg'].push(b);
      }
      // Generic arm detection
      else if (['upper_arm', 'forearm', 'hand'].some(n => name.includes(n))) {
        if (name.includes('.l') || name.endsWith('l') || name.includes('_l')) {
          groups['Left Arm'].push(b);
        } else if (name.includes('.r') || name.endsWith('r') || name.includes('_r')) {
          groups['Right Arm'].push(b);
        } else {
          groups['Other'].push(b);
        }
      }
      // Generic leg detection
      else if (['thigh', 'shin', 'foot'].some(n => name.includes(n))) {
        if (name.includes('.l') || name.endsWith('l') || name.includes('_l')) {
          groups['Left Leg'].push(b);
        } else if (name.includes('.r') || name.endsWith('r') || name.includes('_r')) {
          groups['Right Leg'].push(b);
        } else {
          groups['Other'].push(b);
        }
      }
      else {
        groups['Other'].push(b);
      }
    });
    
    return groups;
  }, [bones]);

  const selectedBoneData = boneRotations[selectedBone] || { x: 0, y: 0, z: 0 };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* 3D Viewport */}
      <div className="lg:col-span-2">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Pose Editor</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={resetPose}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
                {onSavePose && (
                  <Button size="sm" onClick={() => onSavePose(getCurrentPose())}>
                    <Save className="w-4 h-4 mr-1" />
                    Save Pose
                  </Button>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              ref={containerRef} 
              className="w-full rounded-lg overflow-hidden"
              style={{ height: '500px' }}
            >
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10">
                  <div className="text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p className="text-slate-400">Loading model...</p>
                  </div>
                </div>
              )}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10">
                  <p className="text-red-400">{error}</p>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Drag to rotate view • Scroll to zoom • Right-click to pan
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bone Controls */}
      <div className="space-y-4">
        {/* Bone Selector */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Select Bone</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(groupedBones).map(([group, groupBones]) => (
              groupBones.length > 0 && (
                <div key={group}>
                  <p className="text-xs text-slate-500 mb-1">{group}</p>
                  <div className="flex flex-wrap gap-1">
                    {groupBones.map(({ name }) => (
                      <Button
                        key={name}
                        size="sm"
                        variant={selectedBone === name ? "default" : "outline"}
                        className={`text-xs px-2 py-1 h-auto ${
                          selectedBone === name ? 'bg-cyan-600' : 'border-slate-600'
                        }`}
                        onClick={() => setSelectedBone(name)}
                      >
                        {name.replace(/_/g, ' ').replace(/\./g, ' ')}
                      </Button>
                    ))}
                  </div>
                </div>
              )
            ))}
          </CardContent>
        </Card>

        {/* Rotation Controls */}
        {selectedBone && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Rotate: {selectedBone.replace(/_/g, ' ')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* X Rotation */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-red-400">X Rotation</span>
                  <span>{selectedBoneData.x?.toFixed(0) || 0}°</span>
                </div>
                <Slider
                  value={[selectedBoneData.x || 0]}
                  min={-180}
                  max={180}
                  step={1}
                  onValueChange={([val]) => updateBoneRotation(selectedBone, 'x', val)}
                  className="[&_[role=slider]]:bg-red-500"
                />
              </div>

              {/* Y Rotation */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-green-400">Y Rotation</span>
                  <span>{selectedBoneData.y?.toFixed(0) || 0}°</span>
                </div>
                <Slider
                  value={[selectedBoneData.y || 0]}
                  min={-180}
                  max={180}
                  step={1}
                  onValueChange={([val]) => updateBoneRotation(selectedBone, 'y', val)}
                  className="[&_[role=slider]]:bg-green-500"
                />
              </div>

              {/* Z Rotation */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-blue-400">Z Rotation</span>
                  <span>{selectedBoneData.z?.toFixed(0) || 0}°</span>
                </div>
                <Slider
                  value={[selectedBoneData.z || 0]}
                  min={-180}
                  max={180}
                  step={1}
                  onValueChange={([val]) => updateBoneRotation(selectedBone, 'z', val)}
                  className="[&_[role=slider]]:bg-blue-500"
                />
              </div>

              <Button 
                size="sm" 
                variant="outline" 
                className="w-full border-slate-600"
                onClick={() => {
                  updateBoneRotation(selectedBone, 'x', 0);
                  updateBoneRotation(selectedBone, 'y', 0);
                  updateBoneRotation(selectedBone, 'z', 0);
                }}
              >
                Reset This Bone
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick Poses */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Quick Poses</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="border-slate-600"
              onClick={() => {
                // T-Pose - reset all
                resetPose();
              }}
            >
              T-Pose
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="border-slate-600"
              onClick={() => {
                // Arms down pose - find arm bones by name pattern
                resetPose();
                bones.forEach(({ name }) => {
                  const n = name.toLowerCase();
                  if (n.includes('upper_arm') || n.includes('upperarm')) {
                    if (n.includes('.l') || n.includes('_l') || n.endsWith('l')) {
                      updateBoneRotation(name, 'z', -75);
                    } else if (n.includes('.r') || n.includes('_r') || n.endsWith('r')) {
                      updateBoneRotation(name, 'z', 75);
                    }
                  }
                });
              }}
            >
              Arms Down
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="border-slate-600"
              onClick={() => {
                // Wave pose
                resetPose();
                bones.forEach(({ name }) => {
                  const n = name.toLowerCase();
                  if ((n.includes('upper_arm') || n.includes('upperarm')) && 
                      (n.includes('.r') || n.includes('_r') || n.endsWith('r'))) {
                    updateBoneRotation(name, 'z', 150);
                  }
                  if ((n.includes('forearm') || n.includes('lower_arm')) && 
                      (n.includes('.r') || n.includes('_r') || n.endsWith('r'))) {
                    updateBoneRotation(name, 'y', 45);
                  }
                });
              }}
            >
              Wave
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="border-slate-600"
              onClick={() => {
                // Fighting pose
                resetPose();
                bones.forEach(({ name }) => {
                  const n = name.toLowerCase();
                  if (n.includes('upper_arm') || n.includes('upperarm')) {
                    if (n.includes('.l') || n.includes('_l') || n.endsWith('l')) {
                      updateBoneRotation(name, 'x', 60);
                      updateBoneRotation(name, 'z', -30);
                    } else if (n.includes('.r') || n.includes('_r') || n.endsWith('r')) {
                      updateBoneRotation(name, 'x', 80);
                      updateBoneRotation(name, 'z', 30);
                    }
                  }
                  if (n.includes('forearm') || n.includes('lower_arm')) {
                    if (n.includes('.l') || n.includes('_l') || n.endsWith('l')) {
                      updateBoneRotation(name, 'y', -100);
                    } else if (n.includes('.r') || n.includes('_r') || n.endsWith('r')) {
                      updateBoneRotation(name, 'y', 100);
                    }
                  }
                });
              }}
            >
              Fighting
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
